import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { OtterfolioPosition } from "../../lib/otterfolio";
import { PositionTile } from "./PositionTile";
import { ToggleGroup } from "./ToggleGroup";
import { OTTERFOLIO_THEME } from "./theme";

type Sort = "value" | "perf" | "name" | "bruecke";

interface Props {
  positions: OtterfolioPosition[];
  felsValueCents: number;
  raketeValueCents: number;
  onPositionClick: (p: OtterfolioPosition) => void;
}

function sortPositions(list: OtterfolioPosition[], sort: Sort): OtterfolioPosition[] {
  const arr = [...list];
  if (sort === "value") arr.sort((a, b) => b.valueCents - a.valueCents);
  else if (sort === "perf") arr.sort((a, b) => b.returnPercent - a.returnPercent);
  else if (sort === "bruecke")
    arr.sort(
      (a, b) =>
        (b.renditebruecke?.bruecke ?? Number.NEGATIVE_INFINITY) -
        (a.renditebruecke?.bruecke ?? Number.NEGATIVE_INFINITY),
    );
  else arr.sort((a, b) => a.name.localeCompare(b.name));
  return arr;
}

function fmtEur(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

const RESPONSIVE_STYLE = `
  .otterfolio-tile-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 10px;
  }
  @media (min-width: 768px) {
    .otterfolio-tile-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (min-width: 1024px) {
    .otterfolio-tile-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
`;

export function PositionsGrid({
  positions,
  felsValueCents,
  raketeValueCents,
  onPositionClick,
}: Props) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<Sort>("value");

  // Positionen ohne investmentCategory (null) werden bewusst NICHT gerendert:
  // Sie erscheinen weder unter Fels noch unter Rakete, fuehren aber auch zu
  // keinem Absturz. Fuer das MVP akzeptiert; sobald Kategorisierung Pflicht ist,
  // sollte hier ein Fallback- oder Warnpfad entstehen.
  const fels = useMemo(
    () =>
      sortPositions(
        positions.filter((p) => p.investmentCategory === "FELS"),
        sort,
      ),
    [positions, sort],
  );
  const rakete = useMemo(
    () =>
      sortPositions(
        positions.filter((p) => p.investmentCategory === "RAKETE"),
        sort,
      ),
    [positions, sort],
  );

  const sortOptions = [
    { key: "value", label: t("otterfolio.sortByValue", { defaultValue: "Wert" }) },
    { key: "perf", label: t("otterfolio.sortByPerf", { defaultValue: "Perf" }) },
    { key: "name", label: t("otterfolio.sortByName", { defaultValue: "Name" }) },
    { key: "bruecke", label: t("otterfolio.renditebruecke.abbrev", { defaultValue: "Brücke" }) },
  ];

  const felsLabelShort = t("otterfolio.group.fels", { defaultValue: "Fels" });
  const raketeLabelShort = t("otterfolio.group.rakete", { defaultValue: "Rakete" });
  const positionsWord = t("otterfolio.positions", { defaultValue: "Positionen" });

  return (
    <>
      <style>{RESPONSIVE_STYLE}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ToggleGroup options={sortOptions} active={sort} onChange={(v) => setSort(v as Sort)} />
        </div>

        <section>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: OTTERFOLIO_THEME.fels,
              }}
            >
              🪨 {felsLabelShort} · {fels.length} {positionsWord} · {fmtEur(felsValueCents)}
            </div>
          </div>
          <div className="otterfolio-tile-grid">
            {fels.map((p) => (
              <PositionTile
                key={p.id}
                position={p}
                groupColor={OTTERFOLIO_THEME.fels}
                groupLabel={`🪨 ${felsLabelShort}`}
                onClick={onPositionClick}
              />
            ))}
          </div>
        </section>

        <section>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: OTTERFOLIO_THEME.rakete,
              }}
            >
              🚀 {raketeLabelShort} · {rakete.length} {positionsWord} · {fmtEur(raketeValueCents)}
            </div>
          </div>
          <div className="otterfolio-tile-grid">
            {rakete.map((p) => (
              <PositionTile
                key={p.id}
                position={p}
                groupColor={OTTERFOLIO_THEME.rakete}
                groupLabel={`🚀 ${raketeLabelShort}`}
                onClick={onPositionClick}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
